import {
  type DataItem,
  type Mapping,
  pickMappingItemsFromFields,
} from '@pn/core/domain/data';
import { toSIUnit } from '@pn/core/domain/units';
import { ConfigurationError } from '@pn/core/errors';
import type {
  IDataItemMapper,
  IDataItemValueMapper,
} from '@pn/core/mappers/data';
import { assert } from '@pn/core/utils/assert';
import { log } from '@pn/core/utils/debug';
import { isGeoJSONGeometry } from '@pn/services/utils/geojson';
import {
  isBoolean,
  isNil,
  isNumber,
  isObject,
  isString,
  isUndefined,
  keys,
} from 'lodash-es';
import type { ApiDataItem } from './types';

export const apiDataItemMapper = (
  mapping: Mapping
): IDataItemMapper<ApiDataItem> => {
  return {
    toDomainDataItem: (apiDataItem) => {
      assert(
        isUndefined(apiDataItem.internal_id) || // not needed for exports
          isString(apiDataItem.internal_id) ||
          isNumber(apiDataItem.internal_id),
        'API data mapping failed: internal_id must be a (string | number)'
      );

      const logGroupId = `Invalid API response: unmapped fields in ${apiDataItem.internal_id}`;

      const dataItem = pickMappingItemsFromFields(
        mapping,
        keys(apiDataItem),
        logGroupId
      ).reduce<DataItem>(
        (dataItem, mappingItem) => {
          if (mappingItem.field === 'geometry') return dataItem;

          const field = mappingItem['field'];
          const fromValue = apiDataItem[field];
          const toValue = apiDataItemValueMapper.toDomainValue(
            fromValue,
            mappingItem
          );

          dataItem[field] = toValue;
          return dataItem;
        },
        {
          _id: apiDataItem.internal_id,
          geometry: apiDataItem.geometry,
        }
      );

      log(logGroupId).flush();

      return dataItem;
    },
    toTargetDataItem: () => {
      throw new ConfigurationError('Not implemented');
    },
  };
};

export const apiDataItemValueMapper: IDataItemValueMapper<unknown> = {
  toDomainValue: (sourceValue, mappingItem) => {
    if (isNil(sourceValue)) return undefined;

    try {
      switch (mappingItem.domainType) {
        case 'string':
        case 'DateString':
          assert(
            isString(sourceValue),
            getErrorMessage(mappingItem.field, sourceValue, 'string')
          );
          return sourceValue;
        case 'number':
          assert(
            isNumber(sourceValue),
            getErrorMessage(mappingItem.field, sourceValue, 'number')
          );
          return sourceValue;
        case 'boolean':
          assert(
            isBoolean(sourceValue),
            getErrorMessage(mappingItem.field, sourceValue, 'boolean')
          );
          return sourceValue;
        case 'object':
          assert(
            isObject(sourceValue),
            getErrorMessage(mappingItem.field, sourceValue, 'object')
          );
          return sourceValue;
        case 'Geometry':
          assert(
            isGeoJSONGeometry(sourceValue),
            getErrorMessage(mappingItem.field, sourceValue, 'GeoJSON.Geometry')
          );
          return sourceValue;
        case 'SIUnit':
          assert(
            isNumber(sourceValue),
            getErrorMessage(mappingItem.field, sourceValue, 'number')
          );
          return toSIUnit({
            value: sourceValue,
            symbol: mappingItem.domainTypeAttributes.symbol,
          });
        default:
          return mappingItem satisfies never;
      }
    } catch (error) {
      log().warn(String(error));
      return undefined;
    }
  },
  toTargetValue: () => {
    throw new Error('Not implemented');
  },
};

function getErrorMessage(
  field: string,
  sourceValue: unknown,
  expectedType: string
): string {
  return `Bad data in [${field}]: expected ${expectedType}, got ${typeof sourceValue}`;
}
